/**
 * L6 — react-query wrappers around subscriptionApi + announcementsApi.
 *
 * Pattern to propagate to other pages that need to freeze mutations
 * when a tenant is in dunning:
 *
 *   const readOnly = useIsReadOnly()
 *   <Button disabled={readOnly || saving} title={readOnly ? t('read_only') : undefined}>Save</Button>
 *
 * We do NOT retrofit this on the 256 existing routes — L7 will do a
 * systematic pass. L6 just introduces the primitive and demonstrates it
 * on one page.
 */

import { announcementsApi } from '@/api/announcements'
import {
  type SaaSInvoiceStatus,
  subscriptionApi,
} from '@/api/subscription'
import { useAuthStore } from '@/stores/auth.store'
import { useQuery } from '@tanstack/react-query'

export function useSubscription() {
  return useQuery({
    queryKey: ['my', 'subscription'],
    queryFn: () => subscriptionApi.get(),
    staleTime: 60_000,
    networkMode: 'online',
  })
}

export function useMyInvoices(status?: SaaSInvoiceStatus | 'all') {
  return useQuery({
    queryKey: ['my', 'invoices', status ?? 'all'],
    queryFn: () => subscriptionApi.listInvoices(status),
    staleTime: 30_000,
    networkMode: 'online',
  })
}

export function useMyUsage(period?: string) {
  return useQuery({
    queryKey: ['my', 'usage', period ?? 'current'],
    queryFn: () => subscriptionApi.usage(period),
    staleTime: 60_000,
    networkMode: 'online',
  })
}

export function useAnnouncements() {
  return useQuery({
    queryKey: ['announcements', 'active'],
    queryFn: () => announcementsApi.list(),
    staleTime: 60_000,
    networkMode: 'online',
  })
}

/**
 * Returns true when the tenant is in a dunning state where mutations
 * should be blocked (restricted or suspended). Read-only pages stay
 * accessible in every case — this hook only gates writes.
 */
export function useIsReadOnly(): boolean {
  const { data } = useSubscription()
  // L5a — the tenant owner always keeps write access, even under
  // restricted/suspended (plan §2.4 rule 1: no data hostage-taking).
  // The `is_owner` flag comes from /v1/auth/me and is mirrored in the
  // auth store; a null user (loading) defaults to non-owner so we err
  // on the safe side while the shell hydrates.
  const isOwner = (useAuthStore.getState().user as { is_owner?: boolean } | null)?.is_owner === true
  if (isOwner) return false
  const level = data?.license?.dunning_level
  return level === 'restricted' || level === 'suspended'
}
